import { Grid, TextInput, Typography, Checkbox, Switch, GridContainerFullHeight } from '@ts/storybook';
import { JSONValidator, WrapperEntitySelector } from '@components/Inputs';
import { NewPaperWithPadding } from '@components/Misc';

import DeliveryChainTransporterOptions from './options/DeliveryChainTransporterOptions';
import DeliveryChainForwarderOptions from './options/DeliveryChainForwarderOptions';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { CDeliveryChain, CEntity, CLocation, IEntity, IForm } from '@ts/ui-models';

interface IDeliveryChainFormProps extends IForm {
  values: CDeliveryChain;
}

const DeliveryChainForm = (props: IDeliveryChainFormProps) => {
  const { mode, values, onCallbackChange } = props;
  const isViewMode = mode == 'view';

  //#region Handlers
  const onChangeLocation = (location: CLocation | null) => {
    onCallbackChange('parameters.locationId')(location?.getID());
    onCallbackChange('parameters.geometry')(location?.getGeometryCoords());
  };

  const onChangeTransporterForwarder = (field: 'transporter' | 'forwarder') => (entity: CEntity<IEntity> | null) => {
    //TODO: Create modal para avisar que se eliminaran propiedades Edge-ui
    onCallbackChange(field)(entity);
    onCallbackChange(field + 'Id')(entity?.getID());
  };

  //#endregion

  //#region Render
  const renderMainFields = () => {
    return (
      <>
        <Switch
          id="state"
          isReadOnly={isViewMode}
          leftLabel="Enabled"
          checked={values.state === 'ENABLED'}
          onChangeValue={v => onCallbackChange('state')(v === true ? 'ENABLED' : 'DISABLED')}
        />
        <TextInput
          label="Name"
          isReadOnly={isViewMode}
          value={values.name}
          onChangeValue={v => {
            onCallbackChange('name')(v);
            onCallbackChange('customId')(v);
          }}
          required={props.requiredFields.includes('name')}
        />
        <TextInput isReadOnly={isViewMode} label={'Notes'} value={values.notes} onChangeValue={onCallbackChange('notes')} />
      </>
    );
  };

  const renderAgents = () => {
    return (
      <Grid container direction={'column'} spacing={1}>
        <Typography variant="h6" text="Agents" />
        <WrapperEntitySelector
          label={'Inbound channel'}
          searchComponent={{
            component: generateSearchEntitySelector('channel').component,
            configComponent: {
              ...generateSearchEntitySelector('channel').configComponent,
              where: {
                bound: 'inbound',
              },
            },
          }}
          value={values.channel ?? values.channelId}
          onChange={onCallbackChange('channelId')}
          isReadOnly={isViewMode}
          required={props.requiredFields.includes('channelId')}
          multiple={false}
        />
        <WrapperEntitySelector
          label={'Forwarder'}
          searchComponent={generateSearchEntitySelector('forwarder')}
          value={values.forwarder ?? values.forwarderId}
          onChangeEntity={onChangeTransporterForwarder('forwarder')}
          isReadOnly={isViewMode}
          multiple={false}
        />
        <WrapperEntitySelector
          label={'Transporter'}
          searchComponent={generateSearchEntitySelector('transporter')}
          value={values.transporter ?? values.transporterId}
          onChangeEntity={onChangeTransporterForwarder('transporter')}
          isReadOnly={isViewMode}
          multiple={false}
        />
      </Grid>
    );
  };

  const renderOptions = () => {
    return (
      <Grid container direction={'column'} spacing={1}>
        <Typography variant="h6" text="Options" />
        {renderDefaultOptions()}
        <DeliveryChainForwarderOptions {...props} />
        <DeliveryChainTransporterOptions {...props} />
      </Grid>
    );
  };

  const renderDefaultOptions = () => {
    return (
      <>
        <Checkbox
          id="originCoordinatesFilterEnabled"
          label="This filter removes the origin coordinates of the GNSS reference system, where the coordinates are (0,0)."
          checked={values.parameters?.originCoordinatesFilterEnabled ?? true}
          onChangeValue={onCallbackChange('parameters.originCoordinatesFilterEnabled')}
          isReadOnly={isViewMode}
        />
        <Checkbox
          id="geometryFilterEnabled"
          label="This filter removes any GNSS coordinates that fall outside the area defined by the location geometry."
          checked={values.parameters?.geometryFilterEnabled}
          onChangeValue={onCallbackChange('parameters.geometryFilterEnabled')}
          isReadOnly={isViewMode}
        />
        {values.parameters?.geometryFilterEnabled && (
          <WrapperEntitySelector
            searchComponent={generateSearchEntitySelector('location')}
            label="Geometry from location"
            value={values.parameters?.locationId}
            onChangeEntity={entity => onChangeLocation(entity as CLocation)}
            isReadOnly={isViewMode}
          />
        )}
      </>
    );
  };

  //#endregion

  return (
    <GridContainerFullHeight id="delivery-chain-form" className="h-auto" spacing={2} pb={2}>
      <Typography variant="h6" text="Delivery chain" />

      <NewPaperWithPadding className="h-auto">{renderMainFields()}</NewPaperWithPadding>

      <NewPaperWithPadding className="h-auto">
        <Grid container spacing={6}>
          <Grid container direction={'column'} size={{ xs: 12, md: 6 }} spacing={4}>
            {renderAgents()}
            {renderOptions()}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <JSONValidator
              label={'Parameters to send'}
              value={values.parameters}
              disabled={true}
              onJSONValidated={onCallbackChange('parameters')}
            />
          </Grid>
        </Grid>
      </NewPaperWithPadding>
    </GridContainerFullHeight>
  );
};

export default DeliveryChainForm;
